import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import agent from "../../../agent";
import SearchSelect from "../../CalculatorSplit/SearchSelect";

/**
 * Областные центры Казпочты — nsi.kazpost_regional_centers.
 *
 * api-order при регистрации отправления P154 (EMS «Эконом») ставит особую
 * отметку 150, если и отправитель, и получатель в этом списке, иначе 151.
 * Правка действует сразу на новые регистрации, поэтому удаление — через
 * подтверждение.
 */
const KazpostRegionalCenters = () => {
    const toast = React.useRef(null);

    const [centers, setCenters] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const [selected, setSelected] = React.useState({ kato: "" });
    const [adding, setAdding] = React.useState(false);
    const [removingId, setRemovingId] = React.useState(null);

    const showError = (detail) =>
        toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 6000 });
    const showSuccess = (detail) =>
        toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });
    const errorText = (err) => (typeof err === "string" ? err : agent.handleError(err));

    const load = React.useCallback(() => {
        setLoading(true);
        agent.KazpostRegionalCenters.list()
            .then((res) => setCenters(Array.isArray(res) ? res : []))
            .catch((err) => showError(errorText(err)))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const onSearch = React.useCallback((value) =>
        agent.Kato.search(value).then((res) => setOptions(Array.isArray(res) ? res : [])), []);

    const onAdd = () => {
        if (!selected.kato) return;
        setAdding(true);
        agent.KazpostRegionalCenters.add(selected.kato)
            .then((res) => {
                showSuccess(`«${res?.nameRu || selected.kato}» добавлен в список`);
                setSelected({ kato: "" });
                load();
            })
            .catch((err) => showError(errorText(err)))
            .finally(() => setAdding(false));
    };

    const onRemove = (row) => {
        confirmDialog({
            header: "Удаление областного центра",
            icon: "pi pi-exclamation-triangle",
            message: `Убрать «${row.nameRu}» (${row.kato}) из списка? Новые отправления P154 в этот пункт и из него пойдут с отметкой 151.`,
            acceptLabel: "Убрать",
            rejectLabel: "Отмена",
            acceptClassName: "p-button-danger",
            accept: () => {
                setRemovingId(row.id);
                agent.KazpostRegionalCenters.remove(row.id)
                    .then(() => {
                        showSuccess(`«${row.nameRu}» убран из списка`);
                        load();
                    })
                    .catch((err) => showError(errorText(err)))
                    .finally(() => setRemovingId(null));
            },
        });
    };

    const nameBody = (row) => (
        <div>
            <div>{row.nameRu}</div>
            <small style={{ color: "#6b7280" }}>{row.fullNameRu}</small>
        </div>
    );

    const modifiedBody = (row) =>
        row.lastModifiedDate ? String(row.lastModifiedDate).replace("T", " ").slice(0, 16) : "—";

    const actionsBody = (row) => (
        <Button
            icon="pi pi-trash"
            className="p-button-text p-button-sm p-button-danger"
            loading={removingId === row.id}
            onClick={() => onRemove(row)}
            tooltip="Убрать из списка"
            tooltipOptions={{ position: "top" }}
        />
    );

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />

            <h1>Областные центры Казпочты</h1>

            <Message
                severity="info"
                style={{ marginBottom: 10 }}
                text="Отправление EMS «Эконом» (P154) между двумя пунктами из списка регистрируется в Казпочте с отметкой 150, остальные — с отметкой 151. Изменения действуют сразу на новые отправления."
            />

            <div className="p-d-flex p-ai-center" style={{ gap: 8, marginBottom: 10, maxWidth: 720 }}>
                <div style={{ flex: 1 }}>
                    <SearchSelect
                        placeholder="Населённый пункт"
                        name="kazpostRegionalCenter"
                        state={selected}
                        setState={setSelected}
                        options={options}
                        optionValue="kato"
                        optionText="nameRu"
                        onChange={onSearch}
                    />
                </div>
                <Button
                    label="Добавить"
                    icon="pi pi-plus"
                    loading={adding}
                    disabled={!selected.kato}
                    onClick={onAdd}
                />
            </div>
            {selected.kato && <small style={{ color: "#666" }}>КАТО: {selected.kato}</small>}

            <DataTable
                value={centers}
                loading={loading}
                dataKey="id"
                emptyMessage="Список пуст: все отправления P154 уйдут с отметкой 151"
            >
                <Column header="Населённый пункт" body={nameBody} style={{ width: "55%" }} />
                <Column field="kato" header="КАТО" style={{ width: "15%" }} />
                <Column header="Изменён" body={modifiedBody} style={{ width: "20%" }} />
                <Column body={actionsBody} style={{ width: "10%" }} />
            </DataTable>
        </div>
    );
};

export default KazpostRegionalCenters;
